// src/pages/archived/ArchivedProjectsPage.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  RotateCcw,
  Trash2,
  Archive,
  Loader2,
  Eye,
  Filter,
  Info,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { projectsAPI } from '@/services/projects';
import { pipelineStatusesAPI } from '@/services/pipelineStatuses';
import { cn } from '@/lib/utils';

const ArchivedProjectsPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const [selectedStatus, setSelectedStatus] = useState('All');
  const [pipelineStatuses, setPipelineStatuses] = useState([]);
  const [statusOptions, setStatusOptions] = useState(['All']);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const normalizeStatus = (value) => (value || '').trim();

  const getColorConfigByScheme = (colorScheme) => {
    const colorMap = {
      blue: 'bg-sky-50 text-sky-700 border-sky-200',
      orange: 'bg-amber-50 text-amber-700 border-amber-200',
      purple: 'bg-purple-50 text-purple-700 border-purple-200',
      green: 'bg-green-50 text-green-700 border-green-200',
      red: 'bg-red-50 text-red-700 border-red-200',
      slate: 'bg-slate-100 text-slate-600 border-slate-200',
      amber: 'bg-amber-50 text-amber-700 border-amber-200'
    };

    return colorMap[colorScheme] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getIconByName = (iconName) => {
    const iconMap = {
      Info,
      Calendar,
      FileText,
      CheckCircle,
      XCircle,
      Clock,
      AlertCircle
    };

    return iconMap[iconName] || Info;
  };

  const getPipelineStatusMeta = (statusTitle) => {
    const cleanTitle = normalizeStatus(statusTitle);

    const status = pipelineStatuses.find(
      (s) => normalizeStatus(s.title) === cleanTitle
    );

    if (!status) {
      return {
        className: 'bg-gray-50 text-gray-700 border-gray-200',
        icon: Info
      };
    }

    return {
      className: getColorConfigByScheme(
        status.color_scheme || status.colorscheme
      ),
      icon: getIconByName(status.icon_name || status.iconname)
    };
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      setLoading(true);

      const [archivedRes, statusesRes] = await Promise.all([
        projectsAPI.archived(),
        pipelineStatusesAPI.list()
      ]);

      if (archivedRes.success) {
        const archivedProjects = (archivedRes.data || []).map((project) => ({
          ...project,
          status: normalizeStatus(project.status),
          project_name: project.project_name || project.projectName,
          company_name: project.company_name || project.companyName,
          work_type: project.work_type || project.workType,
          serial_number: project.serial_number || project.serialNumber,
          archived_at: project.archived_at || project.archivedAt
        }));

        setArchived(archivedProjects);
      }

      if (statusesRes.success) {
        const statuses = (statusesRes.data || [])
          .filter((s) => Number(s.isactive ?? s.is_active ?? 1) === 1)
          .sort(
            (a, b) =>
              Number(a.displayorder ?? a.display_order ?? 0) -
              Number(b.displayorder ?? b.display_order ?? 0)
          );

        setPipelineStatuses(statuses);

        const dynamicStatuses = statuses
          .map((s) => normalizeStatus(s.title))
          .filter(Boolean);

        setStatusOptions(['All', ...dynamicStatuses]);
      } else {
        setPipelineStatuses([]);
        setStatusOptions(['All']);
      }
    } catch (error) {
      console.error('Failed to load archived page data:', error);
      addNotification('Error', 'Failed to load archived projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id, projectName) => {
    if (!window.confirm(`Restore "${projectName}" to active projects?`)) return;

    try {
      setActionLoading(id);
      const response = await projectsAPI.restore(id);

      if (response.success) {
        setArchived((prev) => prev.filter((p) => p.id !== id));
        addNotification('Success', `"${projectName}" has been restored`, 'success');
      }
    } catch (error) {
      console.error('Failed to restore project:', error);
      addNotification(
        'Error',
        error.message || 'Failed to restore project',
        'error'
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id, projectName) => {
    if (!window.confirm(`Permanently delete "${projectName}"? This action cannot be undone!`)) {
      return;
    }

    try {
      setActionLoading(id);
      const response = await projectsAPI.delete(id);

      if (response.success) {
        setArchived((prev) => prev.filter((p) => p.id !== id));
        addNotification(
          'Warning',
          `"${projectName}" has been permanently deleted`,
          'warning'
        );
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      addNotification(
        'Error',
        error.message || 'Failed to delete project',
        'error'
      );
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const meta = getPipelineStatusMeta(status);
    const StatusIcon = meta.icon;

    return (
      <Badge className={cn('border gap-1.5', meta.className)}>
        <StatusIcon className="w-3.5 h-3.5" />
        {normalizeStatus(status) || 'Unknown'}
      </Badge>
    );
  };

  const filteredProjects =
    selectedStatus === 'All'
      ? archived
      : archived.filter(
          (p) => normalizeStatus(p.status) === normalizeStatus(selectedStatus)
        );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Archived Projects</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Archive className="w-8 h-8 text-gray-600" />
            <div>
              <h1 className="text-3xl font-bold">Archived Projects</h1>
              <p className="text-gray-600 mt-1">
                {filteredProjects.length} of {archived.length}{' '}
                {archived.length === 1 ? 'project' : 'projects'}
              </p>
            </div>
          </div>

          <Button variant="outline" onClick={() => navigate('/projects')}>
            View Active Projects
          </Button>
        </div>

        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Filter className="w-4 h-4" />
                Filter by Status:
              </div>

              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[250px] bg-white">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedStatus !== 'All' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedStatus('All')}
                  className="text-xs"
                >
                  Clear Filter
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Archived Projects List</CardTitle>
          </CardHeader>

          <CardContent>
            {filteredProjects.length === 0 ? (
              <div className="text-center py-12">
                <Archive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">
                  {selectedStatus === 'All'
                    ? 'No archived projects'
                    : `No archived projects with status "${selectedStatus}"`}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {selectedStatus === 'All'
                    ? 'Archived projects will appear here'
                    : 'Try selecting a different status'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">#</TableHead>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Archived Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {filteredProjects.map((project) => (
                      <TableRow key={project.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          #{project.serial_number || '-'}
                        </TableCell>

                        <TableCell>
                          <div className="font-medium">
                            {project.project_name || '-'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {project.work_type || '-'}
                          </div>
                        </TableCell>

                        <TableCell>{project.company_name || '-'}</TableCell>

                        <TableCell>{getStatusBadge(project.status)}</TableCell>

                        <TableCell>{formatDate(project.archived_at)}</TableCell>

                        <TableCell>
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/projects/${project.id}`)}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4 text-gray-600" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                handleRestore(project.id, project.project_name)
                              }
                              disabled={actionLoading === project.id}
                              title="Restore Project"
                            >
                              {actionLoading === project.id ? (
                                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                              ) : (
                                <RotateCcw className="w-4 h-4 text-blue-600" />
                              )}
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                handleDelete(project.id, project.project_name)
                              }
                              disabled={actionLoading === project.id}
                              title="Delete Permanently"
                            >
                              {actionLoading === project.id ? (
                                <Loader2 className="w-4 h-4 animate-spin text-red-600" />
                              ) : (
                                <Trash2 className="w-4 h-4 text-red-600" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {archived.length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <Archive className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-medium mb-1">About Archived Projects</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-800">
                    <li>Archived projects are hidden from the main projects list.</li>
                    <li>You can restore projects at any time.</li>
                    <li>Permanently deleted projects cannot be recovered.</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default ArchivedProjectsPage;